import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Box,
  Typography,
  Collapse,
  IconButton,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material'
import { HealthStatusBadge } from './HealthStatusBadge'
import type { HealthCheck } from '../../../types/healthCheck'

interface HealthCheckRowProps {
  check: HealthCheck
  fpCode: string
}

export const HealthCheckRow: React.FC<HealthCheckRowProps> = ({ check, fpCode }) => {
  const isPending = check.status === 'pending'
  const hasRemediation =
    check.remediation &&
    (check.status === 'watch' || check.status === 'poor' || check.status === 'critical')
  const [showRemediation, setShowRemediation] = useState(false)

  return (
    <Box
      sx={{
        py: 0.75,
        px: 1,
        borderRadius: 1,
        opacity: isPending ? 0.5 : 1,
        '&:hover': isPending ? {} : { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HealthStatusBadge status={check.status} size="small" />
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ flex: 1, color: isPending ? 'text.disabled' : 'text.primary' }}
        >
          {check.name}
        </Typography>
        {check.details_link && !isPending && (
          <IconButton
            component={Link}
            to={check.details_link.replace('{fp_code}', fpCode)}
            size="small"
            sx={{ ml: 'auto' }}
          >
            <OpenIcon fontSize="small" />
          </IconButton>
        )}
        {hasRemediation && (
          <IconButton
            size="small"
            onClick={() => setShowRemediation(!showRemediation)}
          >
            {showRemediation ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        )}
      </Box>

      <Typography
        variant="caption"
        color={isPending ? 'text.disabled' : 'text.secondary'}
        sx={{ display: 'block', ml: 4.5 }}
      >
        {check.summary}
      </Typography>

      {hasRemediation && (
        <Collapse in={showRemediation}>
          <Typography
            variant="caption"
            color="warning.dark"
            sx={{ display: 'block', ml: 4.5, mt: 0.5, fontStyle: 'italic' }}
          >
            {check.remediation}
          </Typography>
        </Collapse>
      )}
    </Box>
  )
}

export default HealthCheckRow
